import { useState, useEffect } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    // SEO JSON-LD pour la page contact
    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "name": "Contact - Les Carnets de Lolotte",
      "description": "Contactez Nathalie pour vos créations personnalisées en papier plié. Commandes sur mesure, questions et devis gratuits.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/contact`,
      "mainEntity": {
        "@type": "Organization",
        "name": "Les Carnets de Lolotte",
        "email": "contact@lescarnetsdelolotte.com",
        "contactPoint": {
          "@type": "ContactPoint",
          "contactType": "customer service",
          "email": "contact@lescarnetsdelolotte.com",
          "availableLanguage": "French"
        }
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(jsonLd);
    document.head.appendChild(script);

    // Mise à jour des meta tags
    document.title = "Contact - Créations personnalisées | Les Carnets de Lolotte";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Contactez Nathalie pour vos créations personnalisées en papier plié. Commandes sur mesure, questions et devis gratuits. Réponse sous 24-48h.');
    }

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.message.length > 500) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('subject', formData.subject);
      formDataToSend.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d3o2er4o3o83kcu36dlg', {
        method: 'POST',
        body: new URLSearchParams(formDataToSend as any)
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8EDEA]">
      {/* Hero Section */}
      <header className="py-20 bg-[#FAF9F8]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-6xl font-bold text-[#B97A72] mb-8" style={{ fontFamily: 'Allura, cursive' }}>
            Contactez-moi
          </h1>
          <p className="text-xl text-[#6B5E5A] leading-relaxed" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Pour toute question ou commande personnalisée, n'hésitez pas à me contacter 💕
          </p>
        </div>
      </header>

      {/* Contact Form */}
      <main className="py-20 bg-[#F8EDEA]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Formulaire */}
            <section className="bg-[#FAF9F8] p-8 rounded-lg border-2 border-[#B97A72] shadow-lg">
              <h2 className="text-3xl font-bold text-[#B97A72] mb-6" style={{ fontFamily: 'Allura, cursive' }}>
                Écrivez-moi
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-6" data-readdy-form id="contact-form">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-[#6B5E5A] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Votre nom *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-[#EBD2CF] rounded-lg focus:border-[#B97A72] focus:outline-none transition-colors text-sm"
                    style={{ fontFamily: 'Poppins, sans-serif' }}
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-[#6B5E5A] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Votre email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-[#EBD2CF] rounded-lg focus:border-[#B97A72] focus:outline-none transition-colors text-sm"
                    style={{ fontFamily: 'Poppins, sans-serif' }}
                  />
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-[#6B5E5A] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Sujet
                  </label>
                  <select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-[#EBD2CF] rounded-lg focus:border-[#B97A72] focus:outline-none transition-colors text-sm pr-8"
                    style={{ fontFamily: 'Poppins, sans-serif' }}
                  >
                    <option value="">Choisissez un sujet</option>
                    <option value="commande">Passer une commande</option>
                    <option value="personnalise">Création personnalisée</option>
                    <option value="question">Question générale</option>
                    <option value="autre">Autre</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-[#6B5E5A] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Votre message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={6}
                    value={formData.message}
                    onChange={handleChange}
                    maxLength={500}
                    className="w-full px-4 py-3 border-2 border-[#EBD2CF] rounded-lg focus:border-[#B97A72] focus:outline-none transition-colors text-sm resize-none"
                    style={{ fontFamily: 'Poppins, sans-serif' }}
                    placeholder="Décrivez votre demande, vos envies, vos questions..."
                  />
                  <p className="text-xs text-[#6B5E5A] mt-1" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    {formData.message.length}/500 caractères
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting || formData.message.length > 500}
                  className="w-full bg-[#B97A72] text-white py-3 px-6 rounded-full font-semibold hover:bg-[#F8EDEA] hover:text-[#B97A72] border-2 border-[#B97A72] transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  {isSubmitting ? 'Envoi en cours...' : 'Envoyer mon message'}
                </button>

                {submitStatus === 'success' && (
                  <div className="p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
                    <p style={{ fontFamily: 'Poppins, sans-serif' }}>
                      Merci pour votre message ! Je vous répondrai dans les plus brefs délais.
                    </p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                    <p style={{ fontFamily: 'Poppins, sans-serif' }}>
                      Une erreur s'est produite. Veuillez réessayer plus tard.
                    </p>
                  </div>
                )}
              </form>
            </section>

            {/* Informations de contact */}
            <aside className="space-y-8">
              <div className="bg-[#FAF9F8] p-8 rounded-lg border-2 border-[#B97A72] shadow-lg">
                <h3 className="text-2xl font-bold text-[#B97A72] mb-6" style={{ fontFamily: 'Allura, cursive' }}>
                  Mes coordonnées
                </h3>
                
                <div className="space-y-4">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-[#B97A72] rounded-full flex items-center justify-center mr-4">
                      <i className="ri-mail-line text-white"></i>
                    </div>
                    <div>
                      <p className="font-medium text-[#6B5E5A]" style={{ fontFamily: 'Playfair Display, serif' }}>Email</p>
                      <p className="text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>contact@lescarnetsdelolotte.com</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-[#B97A72] rounded-full flex items-center justify-center mr-4">
                      <i className="ri-time-line text-white"></i>
                    </div>
                    <div>
                      <p className="font-medium text-[#6B5E5A]" style={{ fontFamily: 'Playfair Display, serif' }}>Délai de réponse</p>
                      <p className="text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>24-48h en moyenne</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-[#FAF9F8] p-8 rounded-lg border-2 border-[#B97A72] shadow-lg">
                <h3 className="text-2xl font-bold text-[#B97A72] mb-6" style={{ fontFamily: 'Allura, cursive' }}>
                  Suivez-moi
                </h3>
                
                <div className="flex space-x-4">
                  <a 
                    href="#" 
                    className="w-12 h-12 bg-[#B97A72] rounded-full flex items-center justify-center text-white hover:bg-[#F8EDEA] hover:text-[#B97A72] border-2 border-[#B97A72] transition-colors"
                  >
                    <i className="ri-facebook-fill text-xl"></i>
                  </a>
                  <a 
                    href="#" 
                    className="w-12 h-12 bg-[#B97A72] rounded-full flex items-center justify-center text-white hover:bg-[#F8EDEA] hover:text-[#B97A72] border-2 border-[#B97A72] transition-colors"
                  >
                    <i className="ri-instagram-line text-xl"></i>
                  </a>
                </div>
                
                <p className="text-[#6B5E5A] mt-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  Découvrez mes dernières créations et l'envers du décor de mon atelier !
                </p>
              </div>

              <div className="bg-[#EBD2CF] p-8 rounded-lg border-2 border-[#B97A72]">
                <h3 className="text-xl font-bold text-[#6B5E5A] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                  💡 Bon à savoir
                </h3>
                <ul className="space-y-2 text-[#6B5E5A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  <li>• Créations personnalisées sur devis</li>
                  <li>• Délai de création : 1 à 2 semaines</li>
                  <li>• Livraison soignée partout en France</li>
                  <li>• Emballage cadeau disponible</li>
                </ul>
              </div>
            </aside>
          </div>
        </div>
      </main>
    </div>
  );
}
